import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent } from "./ui/card";
import { CheckCircle, Warning, Sparkle, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * SubmitReadyScore
 * Shows a 0–100 readiness gauge for a grant draft, plus actionable issues and wins.
 * - Re-fetches when `draftContent` changes (debounced 1.2s) and when `refreshKey` flips.
 * - Free for all tiers — backend endpoint is non-Premium-gated by design (it's a coaching loop).
 */
const ringColor = (score) => {
  if (score >= 85) return "#10B981"; // emerald
  if (score >= 65) return "#84CC16"; // lime
  if (score >= 40) return "#F59E0B"; // amber
  return "#EF4444"; // red
};

const ringLabel = (score) => {
  if (score >= 85) return "Submit-ready";
  if (score >= 65) return "Almost there";
  if (score >= 40) return "Needs work";
  return "Just getting started";
};

export const SubmitReadyScore = ({ applicationId, draftContent, initialScore, refreshKey }) => {
  const [data, setData] = useState(initialScore || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  const lastFetchedContent = useRef("");

  const fetchScore = async () => {
    if (!applicationId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/applications/${applicationId}/submit-ready-score`);
      setData(res.data);
      lastFetchedContent.current = draftContent || "";
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't compute score");
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when refreshKey changes (parent triggers e.g. after auto-save)
  useEffect(() => {
    if (refreshKey === undefined) return;
    fetchScore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Debounced re-fetch on draftContent edits
  useEffect(() => {
    if (!applicationId || draftContent === undefined) return;
    if (draftContent === lastFetchedContent.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchScore();
    }, 1200);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftContent, applicationId]);

  // First-load fetch when initialScore wasn't supplied
  useEffect(() => {
    if (!data && applicationId) fetchScore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  if (!applicationId) return null;

  const score = data?.score ?? 0;
  const color = ringColor(score);
  const label = ringLabel(score);

  // SVG ring math
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <Card
      className="bg-zinc-950 border-zinc-800 mb-4"
      data-testid="submit-ready-score-card"
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-5">
          {/* Score ring */}
          <div className="relative flex-shrink-0" data-testid="submit-ready-score-ring">
            <svg width="96" height="96" viewBox="0 0 96 96">
              <circle
                cx="48"
                cy="48"
                r={radius}
                fill="none"
                stroke="#27272a"
                strokeWidth="8"
              />
              <circle
                cx="48"
                cy="48"
                r={radius}
                fill="none"
                stroke={color}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                transform="rotate(-90 48 48)"
                style={{ transition: "stroke-dashoffset 600ms ease, stroke 300ms ease" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span
                className="text-2xl font-bold"
                style={{ color }}
                data-testid="submit-ready-score-value"
              >
                {score}
              </span>
              <span className="text-[10px] text-zinc-500 -mt-0.5">/ 100</span>
            </div>
          </div>

          {/* Label + summary */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <Sparkle weight="fill" className="w-4 h-4 text-emerald-400" />
                <h3 className="text-sm font-bold tracking-wide uppercase text-zinc-300">
                  Submit-Ready Score
                </h3>
              </div>
              <button
                onClick={fetchScore}
                disabled={loading}
                className="text-xs text-zinc-500 hover:text-emerald-400 flex items-center gap-1 transition-colors disabled:opacity-50"
                data-testid="submit-ready-refresh-btn"
                aria-label="Recompute score"
              >
                <ArrowsClockwise className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                {loading ? "Scoring…" : "Refresh"}
              </button>
            </div>
            <p
              className="text-base font-semibold mb-2"
              style={{ color }}
              data-testid="submit-ready-score-label"
            >
              {label}
            </p>
            {data?.word_count !== undefined && (
              <p className="text-xs text-zinc-500" data-testid="submit-ready-meta">
                {data.word_count} words · {data.placeholders_remaining} placeholder
                {data.placeholders_remaining === 1 ? "" : "s"} left
              </p>
            )}
            {error && (
              <p className="text-xs text-red-400 mt-1">{error}</p>
            )}
          </div>
        </div>

        {/* Issues */}
        {data?.issues?.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="submit-ready-issues">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">
              To reach 100
            </p>
            <ul className="space-y-1.5">
              {data.issues.map((issue, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm text-zinc-300"
                  data-testid={`submit-ready-issue-${i}`}
                >
                  <Warning weight="fill" className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span>{issue}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Wins */}
        {data?.wins?.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="submit-ready-wins">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">
              What's working
            </p>
            <ul className="space-y-1.5">
              {data.wins.map((win, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm text-zinc-400"
                  data-testid={`submit-ready-win-${i}`}
                >
                  <CheckCircle
                    weight="fill"
                    className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5"
                  />
                  <span>{win}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SubmitReadyScore;
